import LoadingButton from '@mui/lab/LoadingButton';
import {
  AppBar,
  Box,
  Button,
  CardMedia,
  Dialog,
  Grid,
  IconButton,
  Stack,
  Typography,
  Theme,
  Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { InlineKeyValue } from '../ApplicationDetails';
import CloseIcon from '@mui/icons-material/Close';
import { cancelPaymentLink, createPaymentLink } from '../../redux-store/actions/payment';
import { useDispatch } from 'react-redux';
import { ApplicationProps } from '../../redux-store/types/api-types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  CancelPaymentResponse,
  CreatePaymentLinkResponse,
  orderPaymentDetails,
} from '../../redux-store/types/payment';
import { formatShortDateAndTime, paymentsConvertedDates } from '../../utils/date';
import { PAYMNETSTATUSMaster } from '../../utils/constant';

import { SxProps } from '@mui/system';
import { getApplicationDetails } from '../../redux-store/actions/application';
import { KeyValue } from '../ApplicationDetails/contributor';
import { currencyConversion } from '../../utils/utilityFunctions';
import { SubHeading } from '../investors/components';
import NumberFormat from 'react-number-format';

const fontColor = (status: string): string => {
  if (PAYMNETSTATUSMaster[status as string] === PAYMNETSTATUSMaster.created) {
    return '#2057A6';
  }
  if (PAYMNETSTATUSMaster[status as string] === PAYMNETSTATUSMaster.failed) {
    return '#F55B76';
  }
  if (
    [PAYMNETSTATUSMaster.cancelled, PAYMNETSTATUSMaster.expired].includes(
      PAYMNETSTATUSMaster[status as string]
    )
  ) {
    return '#B78813';
  }
  if (
    [PAYMNETSTATUSMaster.paid, PAYMNETSTATUSMaster.captured].includes(
      PAYMNETSTATUSMaster[status as string]
    )
  ) {
    return '#4ED795';
  }
  return '#2057A6';
};

function PaymentStatusComponent({ status }: { status: string }): JSX.Element {
  return (
    <Typography
      sx={{
        fontSize: 18,
        fontWeight: 600,
        color: 'text.secondary',
      }}>
      Status:{' '}
      <Typography
        component="span"
        sx={{
          color: fontColor(status),
          fontSize: '16px',
          letterSpacing: '0.4px',
          ml: 0.5,
          fontWeight: 600,
        }}>
        {PAYMNETSTATUSMaster[status as string]}
      </Typography>
    </Typography>
  );
}
function PaymentID({ linkId }: { linkId?: string }): JSX.Element {
  return (
    <Typography
      sx={{
        bgcolor: 'rgba(238, 244, 251,0.7)',
        pr: { xs: 10, sm: 15 },
      }}
      pl={3}>
      <IconButton sx={{ pointerEvents: 'none', padding: '2px' }}>
        <CardMedia component="img" src="/images/link-icon.svg" alt="Link" sx={{ width: '40px' }} />
      </IconButton>
      <Typography
        component="span"
        pl={1}
        sx={{ color: 'text.secondary', fontSize: '16px', fontWeight: 600 }}>
        {linkId}
      </Typography>
    </Typography>
  );
}
function DisplayAmountInDigits({ value }: { value: string | number }): JSX.Element {
  return (
    <NumberFormat
      thousandSeparator={true}
      thousandsGroupStyle="lakh"
      prefix={'₹'}
      value={value}
      displayType="text"
      decimalScale={3}
    />
  );
}

function DisplayFields({ payment }: { payment: CreatePaymentLinkResponse }): JSX.Element {
  return (
    <>
      <Grid item xs={12}>
        <PaymentStatusComponent status={payment.status} />
      </Grid>
      <Typography
        sx={{
          border: '2px solid #ccc',
          borderRadius: '5px',
          mt: 2,
          mb: 3,
          mx: { xs: 0, sm: '30px' },
          color: '#ccc',
          opacity: '0.6',
        }}>
        <PaymentID linkId={payment.paymentLink_id} />
      </Typography>

      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
        rowSpacing={2}>
        <InlineKeyValue
          title={
            PAYMNETSTATUSMaster.paid === PAYMNETSTATUSMaster[payment.status]
              ? 'Paid'
              : 'Amount to be paid'
          }
          description={
            PAYMNETSTATUSMaster.paid === PAYMNETSTATUSMaster[payment.status] ? (
              payment.amount_paid ? (
                <DisplayAmountInDigits value={Number(payment.amount_paid) / 100} />
              ) : (
                payment.amount_paid
              )
            ) : payment.amount ? (
              <DisplayAmountInDigits value={Number(payment.amount) / 100} />
            ) : (
              payment.amount
            )
          }
          amount={
            PAYMNETSTATUSMaster.paid === PAYMNETSTATUSMaster[payment.status]
              ? payment.amount_paid
                ? Number(payment.amount_paid) / 100
                : payment.amount_paid
              : payment.amount
              ? Number(payment.amount) / 100
              : payment.amount
          }
        />
        <KeyValue
          title={'Generated On'}
          description={paymentsConvertedDates(payment.created_at)}
          sx={{ paddingTop: 0 }}
        />
        {PAYMNETSTATUSMaster.cancelled === PAYMNETSTATUSMaster[payment.status] && (
          <KeyValue
            title={'Cancelled On'}
            description={paymentsConvertedDates(payment.cancelled_at)}
          />
        )}
        {PAYMNETSTATUSMaster.expired === PAYMNETSTATUSMaster[payment.status] && (
          <KeyValue title={'Expired On'} description={paymentsConvertedDates(payment.expired_at)} />
        )}
      </Grid>
    </>
  );
}

export function PaymentComponent({
  _application,
}: {
  _application: Partial<ApplicationProps>;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [createApiCall, setCreateApiCall] = useState(false);
  const [application, setApplication] = useState(_application);
  const [paymentDetails, setPaymentDetails] = useState(
    application.paymentLinkDetails?.length ? application.paymentLinkDetails[0] : null
  );
  const dispatch = useDispatch();
  const handleClose = () => {
    setOpen(false);
  };
  const [tooltip, setTooltip] = useState('Click to Copy');
  const CreateRegenerate = ({ text }: { text: string }): JSX.Element => {
    return (
      <LoadingButton
        loadingPosition="start"
        variant="contained"
        loading={createApiCall}
        onClick={async () => {
          try {
            setCreateApiCall(true);

            if (
              PAYMNETSTATUSMaster.created === PAYMNETSTATUSMaster[paymentDetails?.status as string]
            ) {
              (await dispatch(
                cancelPaymentLink(paymentDetails?.paymentLink_id as string)
              )) as unknown as CancelPaymentResponse;
            } else {
              const response = (await dispatch(
                createPaymentLink(application.id as string)
              )) as unknown as CreatePaymentLinkResponse;
            }
            const getApplication = (await dispatch(
              getApplicationDetails(application.id as string)
            )) as unknown as ApplicationProps;
            setPaymentDetails(getApplication.paymentLinkDetails[0]);
            setApplication(getApplication);
          } catch (e) {
            console.error((e as Error).message);
          } finally {
            setCreateApiCall(false);
          }
        }}
        sx={{
          fontSize: 15,
          minWidth: 215,
          fontWeight: 600,
          lineHeight: 1.1,
          py: 1.7,
          my: 2,
        }}>
        {text}
      </LoadingButton>
    );
  };
  return (
    <>
      <Box
        sx={{
          bgcolor: 'white',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
          borderRadius: '10px',
          border: `1px solid ${fontColor(paymentDetails?.status || '')}`,
        }}
        mb={3}>
        <Box sx={{ p: 2 }}>
          <Grid container sx={{ mt: 1, pl: { xs: 2, sm: 10 }, pr: { xs: 2, sm: 10 } }}>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems={paymentDetails ? 'flex-start' : 'center'}
              mb={paymentDetails ? 2 : 1}>
              <Typography sx={{ fontSize: 20, fontWeight: 500, color: 'primary.main' }}>
                Payment Details
              </Typography>
              {paymentDetails ? (
                <PaymentStatusComponent status={paymentDetails.status} />
              ) : (
                <CreateRegenerate text={'Create Payment link'} />
              )}
            </Grid>
            {paymentDetails && (
              <>
                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  mb={2}>
                  <Button
                    variant="text"
                    sx={{
                      border: '2px solid #ccc',
                      borderRadius: '5px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      pr: { xs: 1, sm: 2 },
                      py: 0,
                      pl: 0,
                      ':hover': {
                        backgroundColor: '#fff',
                      },
                      my: 2,
                      color:
                        PAYMNETSTATUSMaster[paymentDetails.status] !== PAYMNETSTATUSMaster.created
                          ? '#ccc'
                          : 'text.secondary',
                      opacity:
                        PAYMNETSTATUSMaster[paymentDetails.status] !== PAYMNETSTATUSMaster.created
                          ? '0.5'
                          : '1',
                    }}
                    disableRipple={true}
                    disabled={
                      PAYMNETSTATUSMaster[paymentDetails.status] !== PAYMNETSTATUSMaster.created
                    }>
                    <PaymentID linkId={paymentDetails.paymentLink_id} />
                    <Tooltip title={tooltip}>
                      <Typography
                        component="span"
                        sx={{ color: '#337FC9', cursor: 'pointer' }}
                        pl={1}
                        onClick={() => {
                          setTooltip('Copied'),
                            navigator.clipboard.writeText(paymentDetails.short_url);
                        }}
                        onMouseOver={() => {
                          setTooltip('Copy Link');
                        }}>
                        <IconButton>
                          <CardMedia component="img" src="/images/copy.svg" alt="Link" />
                        </IconButton>
                        Copy
                      </Typography>
                    </Tooltip>
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      color: 'common.white',
                      fontWeight: 500,
                      lineHeight: 1.1,
                      py: 0.5,
                      pr: 2,
                      my: 2,
                    }}
                    onClick={() => {
                      setOpen(true);
                    }}>
                    {' '}
                    <IconButton>
                      <CardMedia component="img" src="/images/history.svg" alt="Link" />
                    </IconButton>{' '}
                    history
                  </Button>
                  {[PAYMNETSTATUSMaster.expired, PAYMNETSTATUSMaster.cancelled].includes(
                    PAYMNETSTATUSMaster[paymentDetails.status]
                  ) && <CreateRegenerate text={'Regenerate link'} />}
                  {[PAYMNETSTATUSMaster.created].includes(
                    PAYMNETSTATUSMaster[paymentDetails.status]
                  ) && <CreateRegenerate text={'Cancel Payment link'} />}
                </Grid>
                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  mb={2}
                  rowSpacing={2}
                  columnSpacing={13}>
                  <InlineKeyValue
                    title={
                      PAYMNETSTATUSMaster.paid === PAYMNETSTATUSMaster[paymentDetails.status]
                        ? 'Paid'
                        : 'Amount to be paid'
                    }
                    description={
                      PAYMNETSTATUSMaster.paid === PAYMNETSTATUSMaster[paymentDetails.status] ? (
                        paymentDetails.amount_paid ? (
                          <DisplayAmountInDigits value={Number(paymentDetails.amount_paid) / 100} />
                        ) : (
                          paymentDetails.amount_paid
                        )
                      ) : paymentDetails.amount ? (
                        <DisplayAmountInDigits value={Number(paymentDetails.amount) / 100} />
                      ) : (
                        paymentDetails.amount
                      )
                    }
                    amount={
                      PAYMNETSTATUSMaster.paid === PAYMNETSTATUSMaster[paymentDetails.status]
                        ? paymentDetails.amount_paid
                          ? Number(paymentDetails.amount_paid) / 100
                          : paymentDetails.amount_paid
                        : paymentDetails.amount
                        ? Number(paymentDetails.amount) / 100
                        : paymentDetails.amount
                    }
                  />
                  <InlineKeyValue
                    title={'Generated On'}
                    description={paymentsConvertedDates(paymentDetails.created_at)}
                  />
                  {PAYMNETSTATUSMaster.cancelled === PAYMNETSTATUSMaster[paymentDetails.status] && (
                    <InlineKeyValue
                      title={'Cancelled On'}
                      description={paymentsConvertedDates(paymentDetails.cancelled_at)}
                    />
                  )}
                  {PAYMNETSTATUSMaster.expired === PAYMNETSTATUSMaster[paymentDetails.status] && (
                    <InlineKeyValue
                      title={'Expired On'}
                      description={paymentsConvertedDates(paymentDetails.expired_at)}
                    />
                  )}
                  {PAYMNETSTATUSMaster.paid === PAYMNETSTATUSMaster[paymentDetails.status] && (
                    <>
                      <InlineKeyValue
                        title={'Transaction No'}
                        description={paymentDetails.order.paymentdetails[0].bank_transaction_id}
                      />
                      <InlineKeyValue
                        title={'Date Of Transaction'}
                        description={paymentsConvertedDates(
                          paymentDetails.order.paymentdetails[0].created_at
                        )}
                      />
                    </>
                  )}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Box>
      <Dialog
        onClose={handleClose}
        open={open}
        sx={{
          '.MuiPaper-root ': {
            maxWidth: 750,
          },
        }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
            zIndex: 10,
            bgcolor: 'white',
            py: 2,
          }}>
          <IconButton onClick={handleClose} sx={{ float: 'right' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography
            sx={{
              color: 'primary.main',
              fontSize: '25px',
              fontWeight: 600,
              textAlign: 'center',
            }}>
            History Log
          </Typography>
        </Box>
        <Box
          sx={{
            width: { xs: '100%', sm: '90%' },
            mx: 'auto',
            px: { xs: 3, sm: 0 },
            pb: 4,
            mt: 4,
          }}>
          {application.paymentLinkDetails?.map((paymentLinkDetail) => (
            <Grid
              key={paymentLinkDetail.id}
              container
              rowSpacing={1}
              //columnSpacing={5}
              sx={{
                width: '100%',
                ml: 0,
                '.MuiGrid-item': { px: { xs: 0, sm: '30px' } },
                bgcolor: 'white',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                borderRadius: '10px',
                my: 2,
                py: 2,
                px: 1,
              }}>
              {paymentLinkDetail.order ? (
                <>
                  <DisplayFields payment={paymentLinkDetail} />

                  {paymentLinkDetail.order.paymentdetails.length ? (
                    <SubHeading sx={{ letterSpacing: 0.5 + 'px' }}>
                      {' '}
                      Transaction History:
                    </SubHeading>
                  ) : (
                    <></>
                  )}
                  {paymentLinkDetail.order.paymentdetails.map((payment) => (
                    <Grid
                      key={payment.id}
                      container
                      rowSpacing={1}
                      //columnSpacing={5}
                      sx={{
                        width: '100%',
                        ml: 0,
                        '.MuiGrid-item': { px: { xs: 0, sm: '30px' } },
                        bgcolor: 'white',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                        borderRadius: '10px',
                        my: 2,
                        py: 2,
                        px: 1,
                      }}>
                      <Grid item xs={12} mb={1}>
                        <PaymentStatusComponent status={payment.status} />
                      </Grid>

                      <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={2}
                        rowSpacing={2}>
                        <KeyValue title={'Payment Id'} description={payment.paymentId} />

                        {PAYMNETSTATUSMaster.paid === PAYMNETSTATUSMaster[payment.status] && (
                          <>
                            <KeyValue
                              title={'Transaction No'}
                              description={payment.bank_transaction_id}
                            />
                            <KeyValue
                              title={'Date Of Transaction'}
                              description={paymentsConvertedDates(payment.created_at)}
                            />
                          </>
                        )}
                        {PAYMNETSTATUSMaster.failed === PAYMNETSTATUSMaster[payment.status] && (
                          <>
                            <KeyValue
                              title={'Date Of Transaction'}
                              description={paymentsConvertedDates(payment.created_at)}
                            />
                            <KeyValue
                              title={'Reason For Rejection'}
                              description={payment.error_description}
                            />
                          </>
                        )}
                      </Grid>
                    </Grid>
                  ))}
                </>
              ) : (
                <>
                  <DisplayFields payment={paymentLinkDetail} />
                </>
              )}
            </Grid>
          ))}
        </Box>
      </Dialog>
    </>
  );
}

export const PaymentSuccessful = (): JSX.Element => {
  return (
    <Stack
      sx={{
        minHeight: '100vh',
        background: '#f2f6f8 url("/images/login-bg.svg")',
        backgroundSize: 'cover',
      }}>
      <AppBar elevation={0} position="static" sx={{ bgcolor: 'transparent' }}>
        <Box>
          <CardMedia
            component="img"
            src="/images/NEO-LOGO-01.png"
            alt="Logo"
            sx={{ width: '83px', m: 3 }}
          />
        </Box>
      </AppBar>
      <Box
        component="main"
        sx={{
          // height: 'calc(100vh - 43px)',
          flex: 1,
          display: 'flex',
          // placeItems: 'center',
          mt: 13,
          justifyContent: 'center',
        }}>
        <Box
          sx={{
            bgcolor: 'grey.50',
            height: { xs: '310px', sm: '270px' },
            width: 'clamp(300px, 70%, 500px)',
            px: { xs: '25px', sm: '30px' },
            py: '50px',
            mb: 2,
            borderRadius: '10px',
            boxShadow: '0px 3.29214px 6.58427px rgba(27, 27, 27, 0.16)',
          }}>
          <CheckCircleIcon color={'success'} sx={{ fontSize: 30 }} />
          <Typography
            component="h1"
            variant="h5"
            sx={{
              fontSize: 20,
              fontWeight: 600,
              color: 'secondary.main',
              my: '20px',
            }}>
            {'Payment successful'}
          </Typography>
          <Typography
            component="h1"
            variant="h5"
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: 'rgba(0,0,0,0.7)',
            }}>
            {"We'll send you an email with the payment details"}
          </Typography>
          <Typography
            component="h1"
            variant="h5"
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: 'rgba(0,0,0,0.7)',
              mt: '10px',
              mb: '50px',
            }}>
            {'Thank you!'}
          </Typography>
        </Box>
      </Box>
    </Stack>
  );
};
